import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination } from '@mui/material';
import DatasetList from './components/DatasetList';
import DeleteDialog from './components/DeleteDialog';
import ViewRecordsDialog from './components/ViewRecordsDialog';
import Filter from './components/Filter';
import AddDataset from './adddataset';
import AddDatasetModal from './AddDatasetModal';
import EditDatasetModal from './EditDatasetModal';

const Freedatasets = () => {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);

  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState(null);
  const [recordsPage, setRecordsPage] = useState(0);
  const [recordsRowsPerPage, setRecordsRowsPerPage] = useState(10);
  const [recordsTotal, setRecordsTotal] = useState(0);

  const [filter, setFilter] = useState({ id: '', eventName: '' });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editDatasetId, setEditDatasetId] = useState(null);
  const [editEventName, setEditEventName] = useState('');

  const [refreshKey, setRefreshKey] = useState(0); // 🔁 Used to trigger refetch

  const fetchDatasets = async (filterParam = { id: '', eventName: '' }, pageParam = 1, limitParam = 10) => {
    try {
      setLoading(true);
      let url = '/api/v1/freedata/get-freedatasets-list-admin';
      const queryParams = [];

      if (filterParam.id) {
        queryParams.push(`id=${encodeURIComponent(filterParam.id)}`);
      }
      if (filterParam.eventName) {
        queryParams.push(`eventName=${encodeURIComponent(filterParam.eventName)}`);
      }
      queryParams.push(`page=${pageParam}`);
      queryParams.push(`limit=${limitParam}`);

      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error("HTTP error! status: " + response.status);
      }

      const data = await response.json();
      setDatasets(data.data);
      setTotalPages(data.totalPages);
      setPage(data.currentPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async (datasetId, page = 0, rowsPerPage = 10) => {
    try {
      setRecordsLoading(true);
      setRecordsError(null);

      const response = await fetch(
        `/api/v1/freedata/getallfreeDatarecordsflat?page=${page + 1}&limit=${rowsPerPage}&id=${datasetId}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error("HTTP error! status: " + response.status);
      }

      const data = await response.json();
      setRecords(data.data);
      setRecordsTotal(data.total);
    } catch (err) {
      setRecordsError(err.message);
    } finally {
      setRecordsLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasets(filter, page, rowsPerPage);
  }, [filter, page, rowsPerPage, refreshKey]); // 🔁 Add refreshKey dependency

  const handleFilter = (filterObj) => {
    setFilter(filterObj);
    setPage(1);
  };

  const handleOpenDeleteDialog = (id) => {
    setSelectedDatasetId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedDatasetId(null);
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDatasetId) return;

    try {
      const response = await fetch(`/api/v1/freedata/delete-freedatasets/${selectedDatasetId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error("Failed to delete dataset. Status: " + response.status);
      }

      setDatasets(prev => prev.filter(item => item.id !== selectedDatasetId));
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Delete error:', err);
      alert("❌ Error deleting dataset: " + err.message);
    }
  };

  const handleViewClick = (datasetId) => {
    setOpenViewDialog(true);
    setRecordsPage(0);
    fetchRecords(datasetId, 0, recordsRowsPerPage);
    setSelectedDatasetId(datasetId);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setRecords([]);
    setRecordsError(null);
    setSelectedDatasetId(null);
  };

  const handleChangePage = (event, newPage) => {
    setRecordsPage(newPage);
    if (selectedDatasetId) {
      fetchRecords(selectedDatasetId, newPage, recordsRowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRecordsRowsPerPage(newRowsPerPage);
    setRecordsPage(0);
    if (selectedDatasetId) {
      fetchRecords(selectedDatasetId, 0, newRowsPerPage);
    }
  };

  const openAddModal = () => setIsAddModalOpen(true);
  const closeAddModal = () => setIsAddModalOpen(false);

  const openEditModal = (id, eventName) => {
    setEditDatasetId(id);
    setEditEventName(eventName);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditDatasetId(null);
    setEditEventName('');
    setIsEditModalOpen(false);
  };

  const handleEditSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>🔄 Loading datasets...</div>;
  }

  if (error) {
    return <div style={{ color: 'red', textAlign: 'center' }}>❌ Error loading datasets: {error}</div>;
  }

  return (
    <div style={{ padding: '20px', position: 'relative' }}>

      {/* ⛔ TEMPORARY: Feature unavailable overlay — remove this block when re-enabling */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #1e1e2e 0%, #2a2a3e 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '40px 48px',
          textAlign: 'center',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
          maxWidth: '420px',
          width: '90%',
          color: '#fff',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚧</div>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: 700, color: '#f0f0f0' }}>
            Temporarily Unavailable
          </h2>
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
            The <strong style={{ color: '#a78bfa' }}>Free Datasets</strong> feature is currently paused for maintenance.
            It will be back soon.
          </p>
          <div style={{
            marginTop: '24px',
            display: 'inline-block',
            padding: '8px 20px',
            borderRadius: '8px',
            background: 'rgba(167, 139, 250, 0.15)',
            border: '1px solid rgba(167,139,250,0.3)',
            fontSize: '12px',
            color: '#a78bfa',
            letterSpacing: '0.5px',
          }}>
            Contact your administrator for details
          </div>
          <div style={{ marginTop: '20px' }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '10px 28px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(124,58,237,0.4)',
              }}
            >
              ← Back to Overview
            </button>
          </div>
        </div>
      </div>
      {/* ⛔ END TEMPORARY OVERLAY */}

      <h2>📊 Free Datasets</h2>

      <Filter onFilter={handleFilter} />

      <AddDataset onClick={openAddModal} onUploadSuccess={() => setRefreshKey(prev => prev + 1)} />

      <DatasetList
        datasets={datasets}
        onViewClick={handleViewClick}
        onEditClick={(id) => {
          const dataset = datasets.find(d => d.id === id);
          if (dataset) openEditModal(id, dataset.eventName);
        }}
        onDeleteClick={handleOpenDeleteDialog}
      />

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          showFirstButton
          showLastButton
        />
      </div>

      <DeleteDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />

      <ViewRecordsDialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        records={records}
        loading={recordsLoading}
        error={recordsError}
        page={recordsPage}
        rowsPerPage={recordsRowsPerPage}
        total={recordsTotal}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        eventName={datasets.find(d => d.id === selectedDatasetId)?.eventName || ''}
      />

      {isAddModalOpen && (
        <AddDatasetModal
          onClose={closeAddModal}
          onUploadSuccess={() => setRefreshKey(prev => prev + 1)} // 🔁 Refresh on CSV upload
        />
      )}

      {isEditModalOpen && (
        <EditDatasetModal
          open={isEditModalOpen}
          onClose={closeEditModal}
          datasetId={editDatasetId}
          currentEventName={editEventName}
          onUpdateSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default Freedatasets;
